import { createContext, useContext, useMemo } from "react";

type TPermission = string;

interface IHasPermissionOptions {
  requireAll?: boolean;
}

interface IAuthContextValue {
  permissions: Set<TPermission>;
  hasPermission: (
    perm: TPermission | TPermission[],
    options?: IHasPermissionOptions,
  ) => boolean;
}

const AuthContext = createContext<IAuthContextValue | null>(null);

export const AuthProvider = ({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) => {
  const value = useMemo<IAuthContextValue>(() => {
    const permissionSet = new Set(permissions);

    const hasPermission = (
      perm: TPermission | TPermission[],
      options?: IHasPermissionOptions,
    ): boolean => {
      const perms = Array.isArray(perm) ? perm : [perm];

      return options?.requireAll
        ? perms.every((p) => permissionSet.has(p))
        : perms.some((p) => permissionSet.has(p));
    };

    return { permissions: permissionSet, hasPermission };
  }, [permissions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error("useAuth must be used inside <AuthProvider />");

  return context;
};
